import { useEffect, useState } from 'react';
import { Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from './api/client';
import { getCookieBannerText } from './api/cookieBanner';
import { ConfirmationView } from './components/ConfirmationView';
import { CookieBanner } from './components/CookieBanner';
import { ErrorView } from './components/ErrorView';
import { LegalFooter } from './components/LegalFooter';
import { LegalPage } from './components/LegalPage';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { OfferView } from './components/OfferView';
import { VersionBadge } from './components/VersionBadge';
import { I18nProvider, useT } from './hooks/useT';
import { useBrandTheme } from './hooks/useBrandTheme';
import { useOffer } from './hooks/useOffer';
import { useSelect } from './hooks/useSelect';
import { PRE_BOOKING_STATUSES, TERMINAL_UNHAPPY_STATUSES } from './types/dtos';
import type { LegalSlug, TerminalTokenErrorCode } from './types/dtos';

const TERMINAL_TOKEN_CODES = new Set<TerminalTokenErrorCode>([
  'invalid_token_format',
  'invalid_signature',
  'expired',
  'job_not_found',
]);

export function App() {
  return (
    <I18nProvider initialLocale={null}>
      <Routes>
        <Route path="/book/:token" element={<BookingRoute />} />
        <Route path="/impressum" element={<LegalRoute slug="impressum" />} />
        <Route path="/datenschutz" element={<LegalRoute slug="datenschutz" />} />
        <Route path="/terms" element={<LegalRoute slug="terms" />} />
        <Route path="/data-deletion" element={<LegalRoute slug="data-deletion" />} />
        <Route path="*" element={<ErrorView variant={{ kind: 'token', code: 'invalid_token_format' }} />} />
      </Routes>
      <VersionBadge />
    </I18nProvider>
  );
}

/**
 * Legal route wrapper: renders the (anonymous, platform) legal page plus a cookie-consent banner.
 * The banner reads the optional `?tenant=` query param to resolve tenant-overridden text. The banner
 * fetch never blocks the legal content — a failed/absent query simply yields no banner.
 */
function LegalRoute({ slug }: { slug: LegalSlug }) {
  const { locale } = useT();
  const [searchParams] = useSearchParams();
  const tenantParam = searchParams.get('tenant');

  const bannerQ = useQuery({
    queryKey: ['cookie-banner', tenantParam, locale],
    queryFn: () => getCookieBannerText(tenantParam, locale),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return (
    <>
      <LegalPage slug={slug} />
      <CookieBanner text={bannerQ.data?.text ?? null} tenantKey={tenantParam ?? 'platform'} />
    </>
  );
}

function BookingRoute() {
  const { token } = useParams<{ token: string }>();
  if (!token) {
    return <ErrorView variant={{ kind: 'token', code: 'invalid_token_format' }} />;
  }
  return <BookingFlow token={token} />;
}

function BookingFlow({ token }: { token: string }) {
  const { t, setLocale } = useT();
  const offerQ = useOffer(token);
  const selectM = useSelect(token);

  // Apply per-tenant brand colors (CSS custom properties on <html>).
  useBrandTheme(offerQ.data?.branding);

  const [confirmedView, setConfirmedView] = useState<{
    deliveryDateId: string | null;
    extraIds: string[];
    dropOff: boolean;
  } | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // Reset banner when a new offer arrives + sync locale to server-side languagePreference.
  useEffect(() => {
    if (offerQ.data) {
      setBanner(null);
      const head = offerQ.data.languagePreference?.toLowerCase().split('-')[0];
      if (head === 'de' || head === 'en') setLocale(head);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerQ.dataUpdatedAt]);

  return (
    <>
      {renderBookingContent()}
      <LegalFooter imprintHtml={offerQ.data?.imprintHtml} />
      <CookieBanner text={offerQ.data?.cookieBannerText ?? null} tenantKey="platform" />
    </>
  );

  function renderBookingContent() {
    if (offerQ.isLoading) return <LoadingSkeleton />;

    if (offerQ.isError) {
      const err = offerQ.error;
      if (err instanceof ApiError && TERMINAL_TOKEN_CODES.has(err.problem.code as TerminalTokenErrorCode)) {
        return (
          <ErrorView
            variant={{
              kind: 'token',
              code: err.problem.code as TerminalTokenErrorCode,
              ...(err.problem.correlationId !== undefined && { correlationId: err.problem.correlationId }),
            }}
          />
        );
      }
      if (err instanceof ApiError && err.status === 429) {
        return <ErrorView variant={{ kind: 'rate_limited', onRetry: () => offerQ.refetch() }} />;
      }
      if (err instanceof ApiError && err.status >= 500) {
        return (
          <ErrorView
            variant={{
              kind: 'server',
              onRetry: () => offerQ.refetch(),
              code: err.problem.code,
              ...(err.problem.errorId !== undefined && { errorId: err.problem.errorId }),
              ...(err.problem.correlationId !== undefined && { correlationId: err.problem.correlationId }),
            }}
          />
        );
      }
      return <ErrorView variant={{ kind: 'network', onRetry: () => offerQ.refetch() }} />;
    }

    const offer = offerQ.data!;

    // Terminal-unhappy lifecycle
    if (TERMINAL_UNHAPPY_STATUSES.includes(offer.status)) {
      return <ErrorView variant={{ kind: 'lifecycle', status: offer.status as 'Expired' | 'Failed' | 'Superseded' }} />;
    }

    // Booked — render confirmation (either from the GET payload or from a just-completed select)
    if (offer.status === 'Booked' || confirmedView) {
      return (
        <ConfirmationView
          offer={offer}
          selectedDeliveryDateId={confirmedView?.deliveryDateId ?? null}
          selectedExtraIds={confirmedView?.extraIds ?? []}
          dropOff={confirmedView?.dropOff ?? false}
        />
      );
    }

    // Pre-booking — OfferView
    if (PRE_BOOKING_STATUSES.includes(offer.status)) {
      const onSubmit = (payload: {
        deliveryDateId: string | null;
        selectedExtraIds: string[];
        dropOff: { locationOptionId: string | null; note: string | null; termsAccepted: boolean } | null;
      }) => {
        selectM.mutate(
          {
            deliveryDateId: payload.deliveryDateId,
            selectedExtraIds: payload.selectedExtraIds,
            dropOffAuthorization: payload.dropOff,
          },
          {
            onSuccess: () =>
              setConfirmedView({
                deliveryDateId: payload.deliveryDateId,
                extraIds: payload.selectedExtraIds,
                dropOff: payload.dropOff !== null,
              }),
            onError: (err) => handleSelectError(err, () => offerQ.refetch(), setBanner, t),
          },
        );
      };
      return (
        <OfferView
          offer={offer}
          now={new Date()}
          submitting={selectM.isPending}
          banner={banner}
          token={token}
          onSubmit={onSubmit}
        />
      );
    }

    // Fallback (should not happen)
    return <ErrorView variant={{ kind: 'token', code: 'job_not_found' }} />;
  }
}

function handleSelectError(
  err: ApiError,
  refetch: () => void,
  setBanner: (b: string | null) => void,
  t: (path: string) => string,
) {
  const code = err.problem.code;
  if (err.status === 409 && (code === 'already_booked' || code === 'invalid_transition')) {
    refetch();
    return;
  }
  if (err.status === 409 && code === 'date_no_longer_bookable') {
    refetch();
    setBanner(t('banner.deadline_passed'));
    return;
  }
  if (
    err.status === 422 &&
    (code === 'unknown_delivery_date' ||
      code === 'unknown_extra_id' ||
      code === 'unknown_dropoff_location' ||
      code === 'ambiguous_booking_choice' ||
      code === 'no_booking_choice' ||
      code === 'dropoff_not_enabled')
  ) {
    refetch();
    setBanner(t('banner.supersede_warning'));
    return;
  }
  // Network/5xx — banner stays generic
  setBanner(t('error.network'));
}
